<template>
    <SimpleModal
        v-if="showModal"
        @close="$emit('close')"
        class="profile-page-public"
    >
        <template #body>
            <div class="w-100 p-5 profile-page-main d-flex flex-column justify-content-center text-center">
                <div class="owner__photo mb-4 align-self-center">
                    <a
                        :href="`/adverts/posted-by/${user.slug}`"
                        class="owner__avatar"
                        :class="{
                            'owner__avatar--logo': user.is_company,
                        }"
                        title="View all users's adverts">
                        <img v-if="user.image_url" :src="user.image_url" :alt="user.public_name" />
                        <span v-else class="owner__initials">
                            {{ user.public_name_initials }}
                        </span>
                    </a>
                    <UploadPhoto />
                </div>
                <template v-if="user.is_company">
                    <h2>{{ user.company_name }}</h2>
                </template>
                <template v-else>
                    <h2 class="mb-1">{{ user.firstname }} {{ user.lastname }}</h2>
                    <p
                        class="mb-2 additional-status"
                        v-text="
                            user.show_lastname_on_ads ? 'Your last name is visible on advert details.' : 'Your last name is hidden on advert details.'
                        "></p>
                </template>
                <p class="mb-4" v-if="user.created_at">
                    joined <strong> {{ user.created_at }} </strong>
                </p>
            </div>
            <div class="w-100 profile-page-details p-5 text-center d-flex justify-content-center align-items-center">
                <div class="my-auto w-100">
                    <div class="text-large" v-if="user.is_company">
                        <MapPinIcon class="me-2" />
                        {{ user.address || 'N/A' }}
                    </div>

                    <div class="owner__photo mx-auto align-self-center" v-if="user.is_agent && user.company">
                        <a
                            :href="`/adverts/posted-by/${user.company.slug}`"
                            class="owner__avatar owner__avatar--logo"
                            title="View all company's adverts">
                            <img v-if="user.company.image_url" :src="user.company.image_url" :alt="user.company.public_name" />
                            <span v-else class="owner__initials">
                                {{ user.company.public_name_initials }}
                            </span>
                        </a>
                    </div>
                    <br />

                    <span class="text-large" v-if="user.phone_number_e164">
                        <i class="color-brand ri-phone-line"></i>
                        {{ user.phone_number_e164 }}
                    </span>
                    <br />

                    <p
                        v-if="user.is_tenant"
                        class="mt-1 mb-2 additional-status mx-auto"
                        v-text="user.show_phone_on_ads ? 'Your phone is visible on advert details' : 'Your phone is hidden on advert details'"></p>

                    <span class="text-large d-flex gap-2 justify-content-center email-text" v-if="!user.is_company">
                        <i class="color-brand ri-mail-line"></i>
                        {{ user.email }}
                    </span>

                    <template v-if="user.is_individual_tenant">
                        <div class="w-100">
                            Occupation: <br />
                            <span class="secondary-text-color">{{ user.occupation || 'N/A' }} </span>
                            <hr class="w-100 mt-2 mb-2" />
                        </div>

                        <div class="w-100">
                            Household income: <br />
                            <span class="secondary-text-color">{{ user.income_formatted || 'N/A' }}</span>
                            <hr class="w-100 mt-2 mb-2" />
                        </div>

                        <div class="w-100" v-if="user.budget_min || user.budget_max">
                            Budget: <br />
                            <span class="secondary-text-color" v-if="user.budget_min">
                                {{ `£${user.budget_min}` }}
                            </span> →
                            <span class="secondary-text-color" v-if="user.budget_max">
                                {{ `£${user.budget_max}` }}
                            </span>
                            <hr class="w-100 mt-2 mb-2" />
                        </div>

                        <div class="w-100" v-if="user.move_in_earliest_formatted || user.move_in_latest_formatted">
                            Move in: <br />
                            <span class="secondary-text-color" v-if="user.move_in_earliest_formatted">
                                {{ `From ${user.move_in_earliest_formatted}` }}
                            </span>
                            <span class="secondary-text-color" v-if="user.move_in_latest_formatted">
                                {{ `Until ${user.move_in_latest_formatted}` }}
                            </span>
                            <hr class="w-100 mt-2 mb-2" />
                        </div>

                        <div class="w-100" v-if="user.selectedAreas && user.selectedAreas.length > 0">
                            <span class="mb-1">Location: <br /></span>
                            <div class="location-list d-flex flex-row gap-1 mt-2 flex-wrap">
                                <span class="location-item" v-for="area in user.selectedAreas" :key="area.place_id"> {{ area.formatted_name }}</span>
                            </div>
                        </div>
                        <div class="w-100" v-else>
                            Location: <br />
                            <span class="secondary-text-color"> N/A </span>
                        </div>
                    </template>

                    <div class="w-100" v-if="user.is_agent">
                        Occupation: <br />
                        <span class="secondary-text-color"> Agent </span>
                        <hr class="w-100 mt-2 mb-2" />
                    </div>

                    <div class="w-100" v-if="user.is_company">
                        Current Software Provider: <br />
                        <span class="secondary-text-color">{{ user.crm || 'N/A' }} </span>
                        <hr class="w-100 mt-2 mb-2" />
                    </div>

                    <div class="w-100" v-if="user.is_company">
                        Agent Affiliate Group: <br />
                        <span class="secondary-text-color">{{ user.agent_affiliate_group_formatted || 'N/A' }} </span>
                        <hr class="w-100 mt-2 mb-2" />
                    </div>

                    <template v-if="user.is_individual_landlord || user.is_company || user.is_agent">
                        <div class="w-100">
                            Deposit scheme: <br />
                            <span class="secondary-text-color">{{ user.deposit_scheme_formatted || 'N/A' }} </span>
                            <hr class="w-100 mt-2 mb-2" />
                        </div>
                    </template>

                    <div class="w-100" v-if="user.is_individual_landlord">
                        Acredited Landlord Body: <br />
                        <span class="secondary-text-color">
                            {{ user.acredited_landlord_body_formatted || 'N/A' }}
                            <template v-if="user.acredited_landlord_body_description"> - {{ user.acredited_landlord_body_description }} </template>
                        </span>
                        <hr class="w-100 mt-2 mb-2" />
                    </div>

                    <template v-if="user.is_company || user.is_individual_landlord || user.is_agent">
                        <p class="mx-auto" style="max-width: 431px;">
                            About {{ user.is_company ? 'us' : 'me' }}: <br />
                            <span class="user-description">
                                {{ user.description || 'N/A' }}
                            </span>
                        </p>
                    </template>
                </div>
            </div>
        </template>
    </SimpleModal>
</template>

<script>
import { mapState } from 'vuex';
import SimpleModal from '@components/partials/modal/SimpleModal.vue';
import UploadPhoto from '@components/UserProfile/partials/UploadPhoto.vue';

const MapPinIcon = () => import('@components/partials/icons/MapPinIcon.vue');

export default {
    components: {
        UploadPhoto,
        MapPinIcon,
        SimpleModal,
    },
    props: {
        showModal: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            conversationsNotificationsCount: 0,
        };
    },
    computed: {
        ...mapState('user', ['user']),
    },
    watch: {
        user(user) {
            if (!user) {
                return;
            }
            this.conversationsNotificationsCount = user.conversations_notifications_count;
            this.listenForNotifications(user.uuid);
        },
    },
    methods: {
        listenForNotifications(userUuid) {
            window.LaravelEcho.private(`user.chat.notifications.${userUuid}`).listen('.newChatMessage', (e) => {
                const { conversationsNotificationsCount } = e;
                this.conversationsNotificationsCount = conversationsNotificationsCount;
            });
        },
    },
};
</script>
